/**
 * Root store re-exports.
 *
 * Zustand stores are self-contained modules. This file provides a single
 * import point for all store hooks used across the application.
 */

// Filter store — manages article filter state across views
export { useFilterStore, useFiltersAsParams } from '../hooks/useFilters';
export type { FilterState } from '../hooks/useFilters';

// UI store — manages sidebar state and other UI concerns
import { create } from 'zustand';

interface UIState {
  isSidebarOpen: boolean;
  isSidebarCollapsed: boolean;
  activeModal: string | null;
  notifications: NotificationItem[];
  /**
   * Per-page metadata surfaced by the top bar. The header cannot know how many
   * items a tab is showing, so the page publishes it here.
   */
  pageItemCount: number | null;
}

export interface NotificationItem {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: number;
  read: boolean;
}

interface UIActions {
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapse: () => void;
  openModal: (modalId: string) => void;
  closeModal: () => void;
  addNotification: (notification: Omit<NotificationItem, 'id' | 'timestamp' | 'read'>) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;
  setPageItemCount: (count: number | null) => void;
}

export const useUIStore = create<UIState & UIActions>((set) => ({
  isSidebarOpen: true,
  isSidebarCollapsed: false,
  activeModal: null,
  notifications: [],
  pageItemCount: null,

  toggleSidebar: () =>
    set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),

  setSidebarOpen: (open) => set({ isSidebarOpen: open }),

  toggleSidebarCollapse: () =>
    set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),

  openModal: (modalId) => set({ activeModal: modalId }),

  closeModal: () => set({ activeModal: null }),

  addNotification: (notification) =>
    set((state) => ({
      notifications: [
        {
          ...notification,
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          read: false,
        },
        ...state.notifications,
      ].slice(0, 50), // Keep max 50 notifications
    })),

  markNotificationRead: (id) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      ),
    })),

  clearNotifications: () => set({ notifications: [] }),

  setPageItemCount: (count) => set({ pageItemCount: count }),
}));
